<template>
  <v-container class="py-12">
    <v-row>
      <v-col cols="12">
        <div class="title-container">
          <div class="title-block">
            <span class="page-title">Receitas</span>
            <v-breadcrumbs class="app-breadcrumb" divider="›" :items="[
              { text: 'Home', to: '/home' },
              { text: 'Financeiro', to: '/financeiro', exact: true },
              { text: 'Receitas', disabled: true }
            ]" />
          </div>
          <div class="button-group">
            <v-btn outlined color="primary" elevation="0" :loading="exporting" :disabled="!data" @click="exportRevenue">
              <v-icon left small>mdi-file-excel-outline</v-icon>Exportar
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <period-filter :loading="loading" @change="onPeriod" />

    <template v-if="data">
      <div class="stat-grid">
        <stat-card label="Receita líquida" icon="mdi-cash-plus" :value="money(data.totals.net)" color="#1450b5"
          :hint="`bruta ${money(data.totals.revenue)} · pedágios ${money(data.totals.toll)}`" />
        <stat-card label="Viagens finalizadas" icon="mdi-car-clock" :value="number(data.totals.trips)"
          :hint="`${number(data.totals.passengers)} passageiros transportados`" />
        <stat-card label="Ticket médio por viagem" icon="mdi-ticket-percent-outline"
          :value="data.totals.ticket !== null ? money(data.totals.ticket) : '-'" hint="receita líquida ÷ viagens" />
        <stat-card label="Empresas atendidas" icon="mdi-domain" :value="number(data.totals.customers)"
          :hint="topShare" />
      </div>

      <v-card class="section-card">
        <v-card-title><div>Receita por mês<small>Viagens finalizadas, pela data da viagem</small></div></v-card-title>
        <v-card-text>
          <apexchart type="bar" height="280" :options="chart.options" :series="chart.series" />
        </v-card-text>
      </v-card>

      <v-card class="section-card">
        <v-card-title><div>Por empresa<small>Uma OS compartilhada divide a receita entre as empresas dos passageiros</small></div></v-card-title>
        <v-data-table :headers="headers" :items="data.customers" :items-per-page="25" class="app-table" sort-by="net" sort-desc
          no-data-text="Nenhuma viagem finalizada no período" :footer-props="{ itemsPerPageOptions: [25, 50, -1] }">
          <template v-slot:[`item.name`]="{ item }">
            <span class="font-weight-medium">{{ item.name }}</span>
          </template>
          <template v-slot:[`item.revenue`]="{ item }">{{ money(item.revenue) }}</template>
          <template v-slot:[`item.toll`]="{ item }"><span :class="{ 'grey--text': !item.toll }">{{ money(item.toll) }}</span></template>
          <template v-slot:[`item.net`]="{ item }"><strong>{{ money(item.net) }}</strong></template>
          <template v-slot:[`item.share`]="{ item }">
            <div class="share">
              <div class="share__track"><div :style="{ width: Math.max(item.share, 1) + '%' }" /></div>
              <span>{{ percent(item.share) }}</span>
            </div>
          </template>
          <template v-slot:[`item.ticket`]="{ item }">{{ item.trips ? money(item.net / item.trips) : '-' }}</template>
        </v-data-table>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import VueApexCharts from 'vue-apexcharts'
import PeriodFilter from '@/layouts/components/financial/PeriodFilter.vue'

export default {
  name: 'FinancialRevenue',

  components: {
    apexchart: VueApexCharts,
    PeriodFilter
  },

  data: () => ({
    loading: false,
    exporting: false,
    range: null,
    data: null,
    headers: [
      { text: 'Empresa', value: 'name' },
      { text: 'Viagens', value: 'trips', align: 'end' },
      { text: 'Passageiros', value: 'passengers', align: 'end' },
      { text: 'Receita bruta', value: 'revenue', align: 'end' },
      { text: 'Pedágios', value: 'toll', align: 'end' },
      { text: 'Receita líquida', value: 'net', align: 'end' },
      { text: 'Ticket médio', value: 'ticket', align: 'end', sortable: false },
      { text: '% do total', value: 'share', width: 180 }
    ]
  }),

  computed: {
    topShare() {
      const first = this.data && this.data.customers[0]
      return first && first.customer_id ? `maior: ${first.name} (${this.percent(first.share)})` : ''
    },

    chart() {
      const monthly = this.data ? this.data.monthly : []
      return {
        series: [
          { name: 'Receita líquida', data: monthly.map(item => item.net) },
          { name: 'Pedágios', data: monthly.map(item => item.toll) }
        ],
        options: {
          chart: { stacked: true, toolbar: { show: false }, fontFamily: 'Poppins, sans-serif' },
          colors: ['#1450b5', '#9ab6e6'],
          plotOptions: { bar: { columnWidth: '50%', borderRadius: 3 } },
          dataLabels: { enabled: false },
          legend: { position: 'top', horizontalAlign: 'left' },
          xaxis: { categories: monthly.map(item => this.monthLabel(item.month)) },
          yaxis: { labels: { formatter: (value) => 'R$ ' + Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 }) } },
          tooltip: { shared: true, intersect: false, y: { formatter: (value) => this.money(value) } },
          grid: { borderColor: '#eef1f6' }
        }
      }
    }
  },

  methods: {
    async onPeriod(range) {
      this.range = range
      this.loading = true
      try {
        const response = await axios.get('/api/v2/financial/reports/revenue', { params: range })
        this.data = response.data.content
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
      } finally {
        this.loading = false
      }
    },

    async exportRevenue() {
      this.exporting = true
      try {
        const response = await axios.get('/api/v2/financial/reports/revenue/export', { params: this.range, responseType: 'blob' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(new Blob([response.data]))
        link.download = `receitas-${this.range.start}-a-${this.range.end}.xlsx`
        link.click()
        URL.revokeObjectURL(link.href)
      } catch (error) {
        this.$dialog.notify.error('Não foi possível exportar', { position: 'top-right', timeout: 4000 })
      } finally {
        this.exporting = false
      }
    },

    monthLabel(month) {
      const label = moment(`${month}-01`).locale('pt-br').format('MMM/YY')
      return label.charAt(0).toUpperCase() + label.slice(1)
    },

    money(value) {
      return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },

    number(value) {
      return Number(value || 0).toLocaleString('pt-BR')
    },

    percent(value) {
      return `${Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
    }
  }
}
</script>

<style lang="scss" scoped>
.section-card ::v-deep > .v-card__text {
  padding-top: 0;
}

.share {
  display: flex;
  align-items: center;
  gap: 8px;

  span {
    min-width: 44px;
    text-align: right;
    font-size: 12px;
    color: #52627d;
  }
}

.share__track {
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background: #eef1f6;
  overflow: hidden;

  div {
    height: 100%;
    background: #1450b5;
    border-radius: 4px;
  }
}
</style>
